import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { initAnalytics, capturePageview } from './lib/analytics';
import { initSentry, SentryErrorBoundary } from './lib/sentry';
import { AtomsDemo } from './pages/AtomsDemo';
import { OrganismsDemo } from './pages/OrganismsDemo';
import { LoginPage } from './pages/LoginPage';
import { LegalPrivacy } from './pages/LegalPrivacy';
import { LegalDemoNotice } from './pages/LegalDemoNotice';
import Dashboard from './pages/Dashboard';
import { BuildMode } from './pages/BuildMode';
import { BuildReview } from './pages/BuildReview';
import { ClientsList } from './pages/ClientsList';
import { ClientDetail } from './pages/ClientDetail';
import { InvoiceTermsDetail } from './pages/InvoiceTermsDetail';
import { ClientSetupWizard } from './pages/ClientSetupWizard';
import { ParentClientRollup } from './pages/ParentClientRollup';
import { ParentClientsList } from './pages/ParentClientsList';
import { GenericListByConfig } from './pages/GenericList';
import { ContactsListPage } from './pages/ContactsListPage';
import { ContactDetail } from './pages/ContactDetail';
import { UserDetail } from './pages/UserDetail';
import { GenericDetailByConfig } from './pages/GenericDetail';
import { Learning } from './pages/Learning';

import { InvoiceDetail } from './pages/InvoiceDetail';
import { QuickBooksSync } from './pages/QuickBooksSync';
import { OrgSettings } from './pages/OrgSettings';
import { UserManagement } from './pages/UserManagement';
import { ReportsIndex } from './pages/ReportsIndex';
import { ReportViewer } from './pages/ReportViewer';
import { DocumentsList } from './pages/DocumentsList';
import { DocumentDetail } from './pages/DocumentDetail';
import { DocumentReviewQueue } from './pages/DocumentReviewQueue';
import { ActivityLog } from './pages/ActivityLog';
import { AuditLog } from './pages/AuditLog';
import { CustomFieldsAdmin } from './pages/CustomFieldsAdmin';
import { InformationRequests } from './pages/InformationRequests';
import { InformationRequestDetail } from './pages/InformationRequestDetail';
import { FormsLibrary } from './pages/FormsLibrary';
import { ImportWizard } from './pages/ImportWizard';
// V2 GTM surfaces (grafted from merge/be-v2-combined web tree)
import { ReportBuilder } from './pages/ReportBuilder';
import { Notifications } from './pages/Notifications';
// BPP (business personal property) surfaces
import { CalendarPage } from './pages/CalendarPage';
import { ToastProvider, RequireSuperAdmin, RequireEntity } from './organisms';
import { api } from './lib/api';
import { applyOrgBranding, extractOrgBranding } from './lib/branding';

// Design system foundation (Phase 11 Block 6 PR 1)
import './styles/tokens.css';
import './styles/reset.css';
import './styles/global.css';

// Org branding boot — fetch current user (which carries org brand colors per
// Phase 11 Block 6 PR 3b backend wire) + apply CSS-variable overrides on
// the document root before the app first paints. If the fetch fails (e.g.
// not logged in yet), defaults from tokens.css remain in effect.
// Per Populus/doctrine/design-system.md § Color — System vs Brand tokens.
(async () => {
  try {
    const currentUser = await api.get('/api/current-user');
    const branding = extractOrgBranding(currentUser);
    applyOrgBranding(branding);
  } catch {
    // Not authenticated, or org branding not yet wired in API. Defaults apply.
    // Component rendering proceeds with Holdings palette.
  }
})();

// Frontend error tracking — no-op unless VITE_SENTRY_DSN is set at build. First, so it
// captures errors in the branding boot + analytics init too.
initSentry();

// PostHog product analytics — no-op unless VITE_POSTHOG_KEY is set at build.
// Additive + fail-open; identity/group happen on the authenticated side (App.tsx).
initAnalytics();

/**
 * RouteTracker — records a SPA pageview on every React Router location change.
 * PostHog is initialized with capture_pageview:false so we own this signal and
 * it fires on client-side navigation, not just the first load. Fail-open.
 */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    // basename="/" — location.pathname is already the real (root-relative) URL.
    capturePageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

// ignite v2 entry.
//
// Stack: React 18 + TypeScript + Vite + TanStack Query + React Router per
// doctrine/language-policy.md (canonical frontend stack) + UI_REFRESH_MIGRATION.md.
//
// Routing:
//  - basename="/" — V2 owns the site root (FR-6 cutover); all client routes are
//    relative to '/'. Old /v2/* links 301-redirect to the stripped path server-side.
//  - The Express root SPA fallback serves this index.html for any non-API, non-asset
//    navigation, so React Router handles client-side navigation cleanly after load.
//
// API client: TanStack Query consumes /api/* directly (same-origin cookies).

// Toolbar status filter for the Invoice Items list. Mirrors ENUMS.invoice_items.status
// (src/config/enums.js); `pending` is the "staged, not yet on an invoice" state the
// dashboard's Billable-items card counts and deep-links to.
const INVOICE_ITEM_STATUS_FILTER = {
  column: 'status',
  placeholder: 'All statuses',
  options: [
    { value: 'pending', label: 'Pending (billable)' },
    { value: 'billable', label: 'Billable' },
    { value: 'invoiced', label: 'Invoiced' },
    { value: 'paid', label: 'Paid' },
    // "Non billable", not "Non-billable" — the spec's naming rule (assumption 24) calls
    // this label correct as written, and Y3 makes it a status operators actually filter on.
    { value: 'non_billable', label: 'Non billable' },
    { value: 'no_invoice', label: 'No invoice' },
    { value: 'void', label: 'Void' },
  ],
};

// Y3 (Yadira 2026-08-03) — "surface the reason so an operator can see why a win produced
// no bill." GenericList otherwise auto-derives the first 6 columns by displayOrder, which
// for invoice_items is `client_id · property_id · parcel_id · year · state · re_pp` — three
// raw UUIDs, two columns that are always "—", and no money, no status, and neither of the
// columns Y3 exists to show.
//
// A Non billable item carries invoice_id NULL (it is not on an invoice, correctly), so
// InvoiceDetail — the only other page that renders billing_reason — can never display one.
// This list is therefore the ONLY surface for it, and the "Non billable" option in
// INVOICE_ITEM_STATUS_FILTER above already filters to a set the columns could not explain.
const INVOICE_ITEM_COLUMNS = [
  'parcel_id', 'year', 'status', 'description', 'amount', 'billing_reason',
];

// Read-heavy app: every mutation explicitly invalidates its own query keys, so a
// generous staleTime is safe and makes navigation feel instant — a revisited page
// serves cache immediately (revalidated in the background) instead of re-paying the
// ~200ms server round-trip. gcTime keeps unmounted queries in memory long enough
// that clicking back and forth stays a cache hit.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,          // 1 min fresh (was 30s)
      gcTime: 60 * 60_000,        // keep cached data 1h so clicking back-and-forth is a cache hit
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// The query cache is deliberately NOT persisted to localStorage. Persisting it
// rehydrated a PRIOR session's data across a "View as" role-switch / re-login — the
// cache survived the hard reload under non-user-scoped keys (['current-user'],
// ['dashboard'], …), so a switched-into user could briefly see the previous user's
// identity + data (even the operator shell for a client). A hard reload already
// wipes the in-memory cache, so dropping persistence removes that hazard with no
// loss to in-session snappiness — that comes from staleTime/gcTime + hover-prefetch.

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <SentryErrorBoundary fallback={<div style={{ padding: '2rem', fontFamily: 'system-ui' }}>Something went wrong. Please reload.</div>}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/">
        <RouteTracker />
        <ToastProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Legal pages sit OUTSIDE the authenticated <App> tree, alongside
                /login, so a signed-out visitor (or a client-portal user who
                needs the privacy notice before signing in) can reach them. The
                server serves the SPA shell for any unauthenticated navigation,
                so these need no backend route. */}
            <Route path="/legal" element={<Navigate to="/legal/demo" replace />} />
            <Route path="/legal/demo" element={<LegalDemoNotice />} />
            <Route path="/legal/privacy" element={<LegalPrivacy />} />
            <Route path="/__/atoms" element={<AtomsDemo />} />
            <Route path="/__/organisms" element={<OrganismsDemo />} />
            <Route path="/" element={<App />}>
              <Route index element={<Dashboard />} />
              <Route path="build" element={<BuildMode />} />
              <Route path="build/:id/review" element={<BuildReview />} />
              <Route path="calendar" element={<CalendarPage />} />
              {/* Ignite domain — one record type, one route, rendered by the
                  schema-driven generic list/detail. Columns are chosen for what a
                  person scanning the list needs; everything else is on the detail. */}
              <Route path="programs" element={<GenericListByConfig table="programs" title="Programs" columns={['name', 'program_type', 'status']} />} />
              <Route path="programs/:id" element={<GenericDetailByConfig table="programs" title="Programs" />} />
              <Route path="cohorts" element={<GenericListByConfig table="cohorts" title="Cohorts" columns={['name', 'program_id', 'start_date', 'end_date', 'capacity', 'status']} />} />
              <Route path="cohorts/:id" element={<GenericDetailByConfig table="cohorts" title="Cohorts" />} />
              <Route path="enrollments" element={<GenericListByConfig table="enrollments" title="Enrollments" columns={['client_id', 'program_id', 'stage', 'application_date', 'attendance_rate']} />} />
              <Route path="enrollments/:id" element={<GenericDetailByConfig table="enrollments" title="Enrollments" />} />
              <Route path="program-sessions" element={<GenericListByConfig table="program_sessions" title="Sessions" columns={['title', 'kind', 'client_id', 'starts_at', 'status']} />} />
              <Route path="program-sessions/:id" element={<GenericDetailByConfig table="program_sessions" title="Sessions" />} />
              <Route path="attendance" element={<GenericListByConfig table="attendance" title="Attendance" columns={['client_id', 'contact_id', 'status', 'start_date']} />} />

              <Route path="capabilities" element={<GenericListByConfig table="capabilities" title="Capabilities" columns={['code', 'label', 'category', 'status']} />} />
              <Route path="capabilities/:id" element={<GenericDetailByConfig table="capabilities" title="Capabilities" />} />
              <Route path="partners" element={<GenericListByConfig table="partners" title="Partners" columns={['name', 'partner_type', 'city', 'state', 'is_buyer_preferred', 'status']} />} />
              <Route path="partners/:id" element={<GenericDetailByConfig table="partners" title="Partners" />} />
              <Route path="partner-capabilities" element={<GenericListByConfig table="partner_capabilities" title="Partner capabilities" columns={['partner_id', 'capability_id', 'regions']} />} />
              <Route path="referrals" element={<GenericListByConfig table="referrals" title="Referrals" columns={['client_id', 'capability_id', 'partner_id', 'status', 'opened_date']} />} />
              <Route path="referrals/:id" element={<GenericDetailByConfig table="referrals" title="Referrals" />} />
              {/* Learning (migration 008). The overview is bespoke because its
                  value is the JOIN against the instrument; the course/module
                  detail pages are generic CRUD. */}
              <Route path="learning" element={<Learning />} />
              <Route path="courses" element={<GenericListByConfig table="courses" title="Courses" columns={['code', 'title', 'domain', 'level', 'duration_minutes']} />} />
              <Route path="courses/:id" element={<GenericDetailByConfig table="courses" title="Courses" />} />
              <Route path="course-modules" element={<GenericListByConfig table="course_modules" title="Course modules" columns={['title', 'kind', 'duration_minutes']} />} />
              <Route path="course-modules/:id" element={<GenericDetailByConfig table="course_modules" title="Course modules" />} />
              <Route path="course-enrollments" element={<GenericListByConfig table="course_enrollments" title="Course enrolments" columns={['status', 'started_at', 'completed_at']} />} />
              <Route path="course-enrollments/:id" element={<GenericDetailByConfig table="course_enrollments" title="Course enrolments" />} />
              <Route path="assessment-templates" element={<GenericListByConfig table="assessment_templates" title="Instruments" columns={['name', 'version', 'status', 'published_at']} />} />
              <Route path="assessment-templates/:id" element={<GenericDetailByConfig table="assessment_templates" title="Instruments" />} />
              <Route path="assessment-criteria" element={<GenericListByConfig table="assessment_criteria" title="Criteria" columns={['prompt', 'domain', 'response_type', 'weight', 'is_critical']} />} />
              <Route path="assessments" element={<GenericListByConfig table="assessments" title="Assessments" columns={['client_id', 'sequence', 'status', 'composite_score', 'submitted_at']} />} />
              <Route path="assessments/:id" element={<GenericDetailByConfig table="assessments" title="Assessments" />} />

              <Route path="clients" element={<ClientsList />} />
              <Route path="clients/new" element={<ClientSetupWizard />} />
              <Route path="clients/:id" element={<ClientDetail />} />
              <Route path="parent-clients" element={<ParentClientsList />} />
              <Route path="parent-clients/:id" element={<ParentClientRollup />} />
              {/* Government mass update (Q6+Q7): the multi-parcel-selection spine
                  applies exemption flags, caps, OR reductions across a selected set of
                  parcels — the general analogue of the jurisdiction-wide cap. (The
                  jurisdiction profile still applies a cap to every attached parcel.) */}

              {/* Block 9 PRs 1+2: schema-driven list pages for supporting entities.
                  Government reference entities use the bespoke tabbed
                  GovernmentEntityDetail (Overview / Contacts / Activity) — the
                  restored profile UI, not the flat GenericDetail. */}
              {/* 7/28 B1: the Contacts Type filter sources from org-settings contact-types
                  (ContactsListPage), not a hardcoded list. */}
              <Route path="contacts" element={<ContactsListPage />} />
              <Route path="contacts/:id" element={<ContactDetail />} />
              <Route path="documents" element={<DocumentsList />} />
              <Route path="documents/review-queue" element={<DocumentReviewQueue />} />
              <Route path="activity-log" element={<ActivityLog />} />
              <Route path="information-requests" element={<InformationRequests />} />
              <Route path="information-requests/:id" element={<InformationRequestDetail />} />
              <Route path="documents/:id" element={<DocumentDetail />} />
              {/* §8.2: the standalone Transmittals list is retired — a transmittal
                  is a tax-bill status ('transmitted'), searchable from the Tax Bills
                  list. Redirect the old list; individual transmittal detail still
                  resolves for any deep link. */}
              <Route path="invoices" element={<GenericListByConfig table="invoices" title="Invoices" />} />
              <Route path="quickbooks" element={<QuickBooksSync />} />
              <Route path="invoices/:id" element={<InvoiceDetail />} />
              {/* Fee/deal surfaces — super_admin-only by default, grantable via the
                  permissions matrix. Guards render an "Access restricted" panel for
                  a user without the grant (backend also default-denies the data). */}
              {/* The status filter is what makes the dashboard's "Billable items" card
                  provable: it links to /invoice-items?status=pending, and GenericList
                  seeds this filter from the URL so the list shows exactly the items
                  the card counted. */}
              <Route path="invoice-items" element={<RequireEntity entity="invoice_item"><GenericListByConfig table="invoice_items" title="Invoice items" filterField={INVOICE_ITEM_STATUS_FILTER} columns={INVOICE_ITEM_COLUMNS} /></RequireEntity>} />
              <Route path="invoice-items/:id" element={<RequireEntity entity="invoice_item"><GenericDetailByConfig table="invoice_items" title="Invoice items" /></RequireEntity>} />
              <Route path="invoice-terms" element={<RequireEntity entity="invoice_term"><GenericListByConfig table="invoice_terms" title="Invoice terms" /></RequireEntity>} />
              <Route path="invoice-terms/:id" element={<RequireEntity entity="invoice_term"><InvoiceTermsDetail /></RequireEntity>} />
              {/* §8.1: the standalone Values LIST is retired — valuation entry lives
                  inside the parcel profile, so the list redirects to Parcels. The
                  per-id value card (B1) is reachable by drilling from a parcel's
                  Values tab and shows/edits that single tax-year value in place. */}
              {/* §4 — the jurisdiction-line record, to the Siger Tax Record layout. This
                  is where a cap lives (method · cap · starts · ends). Was the generic
                  schema-driven detail, which had no test of its own. */}
              <Route path="users" element={<RequireSuperAdmin><GenericListByConfig table="users" title="Users" disableCreate createCaption="Create users from User Management" /></RequireSuperAdmin>} />
              <Route path="users/:id" element={<RequireSuperAdmin><UserDetail /></RequireSuperAdmin>} />
              <Route path="audit-logs" element={<RequireSuperAdmin><AuditLog /></RequireSuperAdmin>} />
              <Route path="audit-logs/:id" element={<RequireSuperAdmin><GenericDetailByConfig table="audit_logs" title="Audit log" disableEdit /></RequireSuperAdmin>} />

              {/* V2 GTM surfaces */}
              {/* Commission ledger (feature #43). Role-aware surface: the backend
                  returns all-consultants for firm-wide roles and own-only for
                  agent/analyst/consultant, so it is not entity-gated on the FE —
                  the /api/commissions gate is the enforcement. */}
              <Route path="report-builder" element={<ReportBuilder />} />
              <Route path="notifications" element={<Notifications />} />

              {/* BPP returns */}

              {/* Block 10 workflows */}
              <Route path="org-settings" element={<RequireSuperAdmin><OrgSettings /></RequireSuperAdmin>} />
              <Route path="custom-fields" element={<CustomFieldsAdmin />} />
              <Route path="forms" element={<FormsLibrary />} />
              <Route path="imports" element={<ImportWizard />} />
              <Route path="user-management" element={<RequireSuperAdmin><UserManagement /></RequireSuperAdmin>} />
              <Route path="reports" element={<ReportsIndex />} />
              <Route path="reports/:slug" element={<ReportViewer />} />
              <Route path="*" element={<Dashboard />} />
            </Route>
          </Routes>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </SentryErrorBoundary>
  </React.StrictMode>,
);
